import { gql, useQuery } from '@apollo/client';
import { BoxProps, LinkBox, LinkOverlay, Stack, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { EventScheduleInfo } from '@/components/EventScheduleInfo';
import { EventStuffLite } from '@/components/EventStuff';
import { getAppropriateEventLink } from '@/hooks/getAppropriateEventLink';
import { Card } from '@/imports/ui/chakra/components/Card';

import {
  HappeningNow_EventsQuery,
  HappeningNow_EventsQueryVariables,
} from './graphql-types/HappeningNow_EventsQuery';

const EVENTS_QUERY = gql`
  query HappeningNow_EventsQuery($input: HappeningNowInput!) {
    happeningNow(input: $input) {
      results {
        _id
        ...GetAppropriateEventLink_EventFragment
        ...EventStuff_EventFragment
        ...EventScheduleInfo_EventFragment
      }
      hasMore
    }
  }
  ${getAppropriateEventLink.eventFragment}
  ${EventStuffLite.eventFragment}
  ${EventScheduleInfo.eventFragment}
`;

export const HappeningNow: React.FC<BoxProps> = ({ ...boxProps }) => {
  const { data } = useQuery<
    HappeningNow_EventsQuery,
    HappeningNow_EventsQueryVariables
  >(EVENTS_QUERY, {
    variables: {
      input: {
        limit: 5,
        offset: 0,
      },
    },
  });

  if (!data?.happeningNow.results.length) {
    return null;
  }

  return (
    <Card p="4" {...boxProps}>
      <Stack spacing="4">
        <Text color="gray.600" textStyle="compactStrong">
          HAPPENING NOW
        </Text>
        {data.happeningNow.results.map((event) => {
          const eventLink = getAppropriateEventLink(event);

          return (
            <LinkBox key={event._id}>
              <Card p="4" shadow="xs">
                <Stack
                  direction={{ base: 'column', md: 'row' }}
                  justifyContent="space-between"
                >
                  <Stack spacing="1">
                    <LinkOverlay as={NextLink} href={eventLink}>
                      <Text textStyle="xlBodyStrong">{event.name}</Text>
                    </LinkOverlay>

                    <EventScheduleInfo event={event} />
                  </Stack>

                  <EventStuffLite
                    event={event}
                    allowSpecialTooltip={false}
                    allowConferenceJoin={true}
                  />
                </Stack>
              </Card>
            </LinkBox>
          );
        })}
      </Stack>
    </Card>
  );
};
